import { useCallback, useEffect, useRef, useState } from "react";

const ZOOM_STEP = 0.1;

// Owns pan/zoom state for the canvas viewport: wheel-to-zoom (anchored on the
// cursor), middle-mouse-button panning, and screen<->world coordinate mapping.
// initialZoom/initialPan are optional (default to the original 1 / {0,0}) -
// added for TopicCanvasView.jsx's localStorage-restored camera position
// (workspace/students/workspace/TopicCanvasView.jsx), every other caller is
// unaffected.
export default function useCanvasViewport({ minZoom = 0.1, maxZoom = 5, initialZoom = 1, initialPan = { x: 0, y: 0 } } = {}) {
    const viewportRef = useRef(null);

    const [zoom, setZoom] = useState(initialZoom);
    const [pan, setPan] = useState(initialPan);
    const [isPanning, setIsPanning] = useState(false);
    const [panStart, setPanStart] = useState({ x: 0, y: 0 });

    const screenToWorld = useCallback(
        (clientX, clientY) => {
            const rect = viewportRef.current.getBoundingClientRect();
            return {
                x: (clientX - rect.left - pan.x) / zoom,
                y: (clientY - rect.top - pan.y) / zoom,
            };
        },
        [pan, zoom]
    );

    const handleWheel = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            const rect = viewportRef.current.getBoundingClientRect();
            const mouseX = e.clientX - rect.left;
            const mouseY = e.clientY - rect.top;

            const direction = e.deltaY < 0 ? 1 : -1;
            const newZoom = Math.max(minZoom, Math.min(maxZoom, zoom * (1 + direction * ZOOM_STEP)));
            if (newZoom === zoom) return;

            const worldX = (mouseX - pan.x) / zoom;
            const worldY = (mouseY - pan.y) / zoom;

            setPan({
                x: mouseX - worldX * newZoom,
                y: mouseY - worldY * newZoom,
            });
            setZoom(newZoom);
        },
        [zoom, pan, minZoom, maxZoom]
    );

    useEffect(() => {
        const el = viewportRef.current;
        if (!el) return;

        el.addEventListener("wheel", handleWheel, { passive: false });
        return () => el.removeEventListener("wheel", handleWheel);
    }, [handleWheel]);

    const startPan = useCallback(
        (e) => {
            if (e.button !== 1) return;
            e.preventDefault();
            setIsPanning(true);
            setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        },
        [pan]
    );

    const handlePanMove = useCallback(
        (e) => {
            setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
        },
        [panStart]
    );

    const endPan = useCallback(() => setIsPanning(false), []);

    const zoomBy = useCallback(
        (factor) => {
            setZoom((z) => Math.max(minZoom, Math.min(maxZoom, z * factor)));
        },
        [minZoom, maxZoom]
    );

    const resetView = useCallback(() => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    }, []);

    return {
        viewportRef,
        zoom,
        pan,
        isPanning,
        screenToWorld,
        startPan,
        handlePanMove,
        endPan,
        zoomBy,
        resetView,
    };
}
